import React, { createContext, ReactNode, useState, useEffect, useContext } from "react";
import axios from "axios";
import { IUserDetails, PaymentMethod, SUBSCRIPTION_TYPE } from "./types";
import { createUser, fetchUser } from "../api/users";
import { createAccessTrial } from "../api/user-server";
import { useAuth } from "./authContext";

export interface IDataContextValue {
  user: IUserDetails | null;
  isLoading: boolean;
  buyAccess: (duration: SUBSCRIPTION_TYPE, paymentMethod?: PaymentMethod) => Promise<void>;
  fetchData: () => Promise<void>;
  updateData: () => Promise<void>;
}

export const DataContext = createContext<IDataContextValue | null>(null);

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider = ({ children }: DataProviderProps) => {
  const { status } = useAuth();
  const [userDetail, setUserDetail] = useState<IUserDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Загружаем VPN-профиль только когда auth завершён.
  // На guest — обнуляем, чтобы при логауте не оставалось чужих данных.
  useEffect(() => {
    if (status === "loading") {
      return;
    }
    if (status === "guest") {
      setUserDetail(null);
      setIsLoading(false);
      return;
    }
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const loadUserProfile = async (): Promise<boolean> => {
    try {
      const user = await fetchUser();
      setUserDetail(user);
      return true;
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        console.error("Error fetching user:", error);
        return false;
      }
    try {
        const user = await createUser();
        setUserDetail(user);
        return true;
      } catch (fallbackError) {
        console.error("Error loading user profile:", fallbackError);
        return false;
      }
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await loadUserProfile();
    } finally {
      setIsLoading(false);
    }
  };

  const updateData = async () => {
    if (status !== "authed") return;
    setIsLoading(true);
    try {
      await loadUserProfile();
    } finally {
      setIsLoading(false);
    }
  };

  const buyAccess = async (subscriptionType: SUBSCRIPTION_TYPE, paymentMethod: PaymentMethod = "sbp") => {
    try {
      if (userDetail?.id) {
        if (subscriptionType === SUBSCRIPTION_TYPE.TEST) {
          const response = await createAccessTrial({ userId: userDetail.id });
          setUserDetail(response);
        }
      }
    } catch (error) {
      console.error("Error buying access:", error);
      throw error;
    }
  };

  const value = {
    user: userDetail,
    isLoading,
    buyAccess,
    fetchData,
    updateData
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
